import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import { Box, Theme } from '@mui/material';
import { PropsWithChildren, useEffect } from 'react';

import StyledSnackbarProvider from '@/@mantis/components/third-party/Notistack';
import DrawerProvider from '@/@mantis/contexts/DrawerContext';
import ThemeCustomization from '@/@mantis/theme';
import { AlertOctagonIcon } from '@/assets/icons/AlertOctagonIcon';
import { AlertTriangleIcon } from '@/assets/icons/AlertTriangleIcon';
import { CheckCircleBrokenIcon } from '@/assets/icons/CheckCircleBrokenIcon';
import { InfoCircleIcon } from '@/assets/icons/InfoCircleIcon';

import NotistackCloseIcon from './NotistackCloseIcon';

interface Props extends PropsWithChildren {
  themeOverrides?: (theme: Theme) => Theme;
}

const MuiPageWrapper: React.FC<Props> = ({ children, themeOverrides }) => {
  // Apply scrolling fix when MUI page wrapper mounts
  useEffect(() => {
    // Ensure Ionic doesn't interfere with body scrolling
    document.body.style.position = 'static';
    document.body.style.overflow = 'auto';
    document.body.style.overflowY = 'auto';
    document.body.style.height = 'auto';
    document.body.style.contain = 'none';
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.height = '100%';

    return () => {
      // Clean up styles if component unmounts
      document.body.style.position = '';
      document.body.style.overflow = '';
      document.body.style.overflowY = '';
      document.body.style.height = '';
      document.body.style.contain = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
    };
  }, []);

  return (
    <ThemeCustomization themeOverrides={themeOverrides}>
      <DrawerProvider>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100%',
            overflowX: 'hidden',
            position: 'relative',
            width: '100%',
            maxWidth: '100%',
            margin: 0,
            padding: 0,
            backgroundColor: 'background.paper',
          }}
        >
          {children}
        </Box>
        <StyledSnackbarProvider
          maxSnack={5}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          action={NotistackCloseIcon}
          iconVariant={{
            default: <InfoCircleIcon />,
            error: <AlertOctagonIcon />,
            success: <CheckCircleBrokenIcon />,
            warning: <AlertTriangleIcon />,
            info: <InfoCircleIcon />,
          }}
        />
      </DrawerProvider>
    </ThemeCustomization>
  );
};

export default MuiPageWrapper;
